import Cookies from 'js-cookie';

import { fetchAndCacheWithoutPage } from '@/components/shared/CacheStroage/CacheWithoutPage';
import { trackAddToCartEvent } from '@/components/shared/Gtags';

import { addLocalPrefix, getAllByPrefix } from '@/utils/LocalStorage';

import { apiClient, baseForPdf } from '../apiClients';
import { getUserProfile } from '../user';
import {
  getLocalUserId,
  getLoginToken,
  getUserId,
  getZl_UID,
  removeMarketingData,
  setUserId,
} from '../../components/shared/getToken';
import { Endpoints } from '../../network';
import { AddToCartProps, UpdateCartProps } from '../../utils/interfaces/cart';

interface ICartCount {
  id?: string;
  localId?: string;
  cartId?: number;
}

type CartCountParams =
  | { temp_user_id: string | undefined; user_id?: undefined; cart_id?: number }
  | { user_id: string | undefined; temp_user_id?: undefined; cart_id?: number };

type CartSummaryParams = {
  cart_id?: number;
  user_id?: string;
  temp_user_id?: string;
};

export const getPayload = async (data: any) => {
  try {
    const [token, userId, localUserId] = await Promise.all([
      getLoginToken(),
      getUserId(),
      getLocalUserId(),
    ]);
    const isValidUserId = userId && userId !== 'undefined' && userId !== 'null';

    let finalUserId = isValidUserId ? userId : undefined;

    if (token && !finalUserId) {
      const data = await getUserProfile();

      const resolvedUserId = data?.data?.id;

      if (resolvedUserId) {
        finalUserId = String(resolvedUserId);
        setUserId(finalUserId);
      }
    }
    return !token
      ? { ...data, temp_user_id: localUserId }
      : { ...data, user_id: finalUserId };
  } catch (error) {
    console.error('Error getting token or user ID:', error);
  }
};

export const getCartDetails = async (id: string, cartId?: number) => {
  let payload = await getPayload({ temp_user_id: id });
  if (cartId) payload = { ...payload, cart_id: cartId };
  const response = await apiClient.post(`${Endpoints.getCartDetails}`, payload);
  return response?.data;
};

export const deleteCartItem = async (id: string, cartId?: number) => {
  const localUserId = await getLocalUserId();
  const payload = await getPayload({ temp_user_id: localUserId });
  const response = await apiClient.delete(`${Endpoints.getCartDetails}/${id}`, {
    ...payload,
    ...(cartId && { cart_id: cartId }),
  });
  return response?.data;
};

export const mergeAllCarts = async () => {
  const localUserId = await getLocalUserId();
  const payload = await getPayload({ temp_user_id: localUserId });
  const response = await apiClient.post(`${Endpoints.mergeAllCarts}`, {
    ...payload,
  });
  return response?.data;
};

export const getCartSummary = async (cartId?: number) => {
  const [token, userId, localUserId] = await Promise.all([
    getLoginToken(),
    getUserId(),
    getLocalUserId(),
  ]);

  const isValidUserId = userId && userId !== 'undefined' && userId !== 'null';

  let finalUserId = isValidUserId ? userId : undefined;

  if (token && !finalUserId) {
    const data = await getUserProfile();

    const resolvedUserId = data?.data?.id;

    if (resolvedUserId) {
      finalUserId = String(resolvedUserId);
      setUserId(finalUserId);
    }
  }
  let params: CartSummaryParams;
  params = !token ? { temp_user_id: localUserId } : { user_id: finalUserId };
  if (cartId) {
    params = { ...params, cart_id: cartId };
  }

  const response = await apiClient.get(`${Endpoints.cartSummary}`, { params });
  return response?.data;
};

export const updateCartAddress = async (
  shipping_address_id: number,
  cartId?: number,
  billing_address_id?: number,
  billing_gst_number?: string,
) => {
  const payload = await getPayload({ shipping_address_id });
  if (cartId) payload.cart_id = cartId;
  if (billing_address_id) payload.billing_address_id = billing_address_id;
  if (billing_gst_number) payload.billing_gst_number = billing_gst_number;
  if (shipping_address_id) {
    const response = await apiClient.post(
      `${Endpoints.updateCartAddress}`,
      payload,
    );
    return response?.data;
  }
};

export const addCouponCode = async (coupon_code: string, cartId?: number) => {
  const payload = await getPayload({ coupon_code });
  const response = await apiClient.post(`${Endpoints.applyCoupon}`, {
    ...payload,
    ...(cartId && { cart_id: cartId }),
  });
  return response?.data;
};

export const addToRecentlyViewed = async (
  productId: string | number,
  zl_uid?: string,
) => {
  try {
    const localUserId = await getLocalUserId();

    const response = await baseForPdf.post(`${Endpoints.recentlyViewed}`, {
      productId,
      tempUserId: localUserId,
      ...(zl_uid && { zl_uid }),
    });

    return response?.data;
  } catch {
    return null;
  }
};

// export const appendMarketingData = (payload: any) => {
//   if (Cookies.get('marketing_data')) {
//     payload.marketing_data = JSON.parse(
//       Cookies.get('marketing_data') as string,
//     );
//   }
//   return payload;
// };

export const appendMarketingDataWithLocalStorageData = (payload: any) => {
  // Step 1: Load existing cookie data
  const existingMarketingData = {};

  // if (Cookies.get('marketing_data')) {
  //   existingMarketingData = JSON.parse(Cookies.get('marketing_data') as string);
  // }

  // Step 2: Load localStorage data and prefix keys
  const marketingDataFromLocalStorage = getAllByPrefix([
    'afc_',
    'ref_',
    'utm_',
  ]);
  const marketingDataFromLocalStorageWithLocalPrefix = addLocalPrefix(
    marketingDataFromLocalStorage,
  );

  // Step 3: Merge both into payload.marketing_data
  if (
    Object.keys(existingMarketingData || {}).length ||
    Object.keys(marketingDataFromLocalStorageWithLocalPrefix || {}).length
  ) {
    payload.marketing_data = {
      ...existingMarketingData,
      ...marketingDataFromLocalStorageWithLocalPrefix,
    };
  }

  return payload;
};

export const updatecartNitroEvent = async (cartId: number) => {
  try {
    const localStorageUserId = localStorage.getItem('userId') as string;

    const result = await getCartDetails(localStorageUserId, cartId);
    if (
      typeof (window as any).nitro !== 'undefined' &&
      typeof (window as any).nitro.track === 'function' &&
      result
    ) {
      const zebra_selected_country = JSON?.parse(
        localStorage?.getItem?.('zebra_selected_country') as string,
      )?.value?.country;
      const utmData: Record<string, string> = {};
      const searchParams = new URLSearchParams(window?.location?.search);
      searchParams.forEach((value, key) => {
        if (key.startsWith('utm_')) {
          utmData[key] = value;
        }
      });
      const updatecartData = {
        cart_url: process.env.NEXT_PUBLIC_API_URL_APP_URL + '/cart/' || '',
        cart_id: result?.cart_id,
        country: zebra_selected_country?.name,
        currency: zebra_selected_country?.currency,
        line_items: result?.data?.[0]?.cart_items?.map((i: any) => ({
          product_id: i?.product_id,
          title: i?.display_name,
          quantity: i?.quantity,
          line_price: i?.price,
          currency: zebra_selected_country?.currency,
          image_url: i?.product_thumbnail_image,
        })),
        cart_value: result?.grand_total,
        utm_data: utmData,
      };

      nitro.track('updatecart', updatecartData);
    }
  } catch (error) {
    console.error(error);
  }
};

const addtocartNitroEvent = (response: any) => {
  try {
    const cartData = response?.data?.data;
    if (
      typeof (window as any).nitro !== 'undefined' &&
      typeof (window as any).nitro.track === 'function' &&
      cartData
    ) {
      const zebra_selected_country = JSON?.parse(
        localStorage?.getItem?.('zebra_selected_country') as string,
      )?.value?.country;
      const utmData: Record<string, string> = {};
      const searchParams = new URLSearchParams(window?.location?.search);
      searchParams.forEach((value, key) => {
        if (key.startsWith('utm_')) {
          utmData[key] = value;
        }
      });
      const addtocartData = {
        cart_url: process.env.NEXT_PUBLIC_API_URL_APP_URL + '/cart/' || '',
        cart_id: cartData?.cart_id,
        quantity: cartData?.quantity,
        title: cartData?.display_name,
        price: cartData?.product?.unit_price,
        product_id: cartData?.product?.id,
        image_url:
          process.env.NEXT_PUBLIC_IMAGE_BASE_URL_ECOM +
          '/' +
          cartData?.product?.thumbnail?.file_name,
        country: zebra_selected_country?.name,
        currency: zebra_selected_country?.currency,
        utm_data: utmData,
      };

      nitro.track('addtocart', addtocartData);
      updatecartNitroEvent(cartData?.cart_id);
    }
  } catch (error) {
    console.error(error);
  }
};

export const addToCart = async (
  data: AddToCartProps,
  isBuyNowParam?: boolean,
) => {
  const isBuyNow = data.isBuyNow ?? isBuyNowParam;
  const { trackingData, isBuyNow: _isBuyNow, ...apiData } = data;

  let payload = await getPayload(apiData);

  if (isBuyNow) {
    payload = { ...payload, buy_now: true };
  }

  const response = await apiClient.post(
    `${Endpoints.addToCart}`,
    appendMarketingDataWithLocalStorageData(payload),
  );

  addtocartNitroEvent(response);

  console.log({ trackingData });

  if (trackingData) {
    try {
      const zl_uid = trackingData.userData?.zl_uid || (await getZl_UID());
      const fallbackUserData =
        trackingData.userData || (zl_uid ? ({ zl_uid } as any) : undefined);
      const pathname =
        trackingData.pathname ||
        (typeof window !== 'undefined' ? window.location.pathname : '');

      console.log({ thisisdata: trackingData.productData });

      await trackAddToCartEvent({
        productData: trackingData.productData,
        userData: fallbackUserData,
        pathname,
        productType: trackingData.productType,
        type: trackingData.type,
      });
    } catch (trackingError) {
      console.error('Error tracking add to cart event:', trackingError);
    }
  }

  return response?.data;
};

export const updateCartProductQuantity = async (
  data: UpdateCartProps,
  cart: any,
) => {
  const payload = await getPayload(data);
  const response = await apiClient.post(`${Endpoints.updateQuantity}`, payload);
  updatecartNitroEvent(cart.cartId);
  return response?.data;
};

export const getCartCount = async ({ id, localId, cartId }: ICartCount) => {
  const [token, userId, localUserId] = await Promise.all([
    getLoginToken(),
    getUserId(),
    getLocalUserId(),
  ]);

  const isValidUserId = userId && userId !== 'undefined' && userId !== 'null';

  let finalUserId = isValidUserId ? userId : undefined;

  if (token && !finalUserId) {
    const data = await getUserProfile();

    const resolvedUserId = data?.data?.id;

    if (resolvedUserId) {
      finalUserId = String(resolvedUserId);
      setUserId(finalUserId);
    }
  }

  const params: CartCountParams = localId
    ? { temp_user_id: localId }
    : id
      ? { user_id: id }
      : !token
        ? { temp_user_id: localUserId }
        : { user_id: finalUserId };

  if (cartId) params.cart_id = cartId;

  const response = await apiClient.get(`${Endpoints.getCartCount}`, { params });
  return response?.data;
};

export const placeOrder = async (
  payment_type: string,
  cartId?: number,
  billing_address_id?: number,
  billing_gst_number?: string,
  shipping_address_id?: number,
) => {
  const payload: {
    payment_type: string;
    cart_id?: number;
    billing_address_id?: number;
    billing_gst_number?: string;
    shipping_address_id?: number;
  } = {
    payment_type,
    billing_address_id,
    billing_gst_number,
    shipping_address_id,
  };

  if (cartId) payload.cart_id = cartId;

  const response = await apiClient.post(
    `${Endpoints.placeOrder}`,
    appendMarketingDataWithLocalStorageData(payload),
  );

  if (Cookies.get('marketing_data')) {
    await removeMarketingData();
  }

  return response?.data;
};

export const placePreOrder = async (payment_type: string, cartId?: number) => {
  const payload: {
    payment_type: string;
    order_from: string;
    cart_id?: number;
  } = {
    payment_type,
    order_from: 'web',
  };

  if (cartId) payload.cart_id = cartId;

  const response = await apiClient.post(
    `${Endpoints.placePreOrder}`,
    appendMarketingDataWithLocalStorageData(payload),
  );

  // if (Cookies.get('marketing_data')) {
  //   await removeMarketingData();
  // }

  return response?.data;
};
export const createRazorpayPayment = async (
  data: {
    order_id: string;
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  },
  cartId?: number,
) => {
  const response = await apiClient.post(`${Endpoints.razorpayPayment}`, {
    ...data,
    ...(cartId && { cart_id: cartId }),
  });

  return response?.data;
};

export const handlePaymentFailed = async (
  data: {
    order_id: string;
    razorpay_order_id: string;
  },
  cartId?: number,
) => {
  const response = await apiClient.post(`${Endpoints.paymentFailed}`, {
    ...data,
    ...(cartId && { cart_id: cartId }),
  });
  return response?.data;
};

export const handleConvertToCod = async (
  data: { order_id: string },
  cartId?: number,
) => {
  const response = await apiClient.post(`${Endpoints.convertToCod}`, {
    ...data,
    ...(cartId && { cart_id: cartId }),
  });
  return response?.data;
};

export const checkEmailExist = async (email: string) => {
  const response = await apiClient.post(`${Endpoints.emailExist}`, { email });
  return response?.data;
};

export const getOrderDetails = async (orderId: string) => {
  const response = await apiClient.get(`${Endpoints.orderDetails}/${orderId}`);

  return response?.data?.data;
};

export const handleRemoveCoupon = async (
  coupon_code: string,
  cartId?: number,
) => {
  const payload = await getPayload({ coupon_code });
  const response = await apiClient.post(`${Endpoints.removeCoupon}`, {
    ...payload,
    ...(cartId && { cart_id: cartId }),
  });
  return response?.data;
};

export const handleCheckCoupon = async (cartId?: number) => {
  const response = await apiClient.post(`${Endpoints.checkCoupon}`, {
    ...(cartId && { cart_id: cartId }),
  });
  return response?.data;
};

export const getAllCoupons = async () => {
  const response = await baseForPdf.get(Endpoints.getAllCoupons);
  return response?.data;
};

export const getRazorpayHashKeyData = async (id: string) => {
  const hashKey = encodeURIComponent(id as string);
  const response = await apiClient.get(
    `${Endpoints.getRazorpayHashKeyData}/${hashKey}`,
  );
  return response?.data;
};

export const getRazorpayHashKeyVerify = async (
  id: string,
  rzp_payment_id: string,
) => {
  try {
    const hashKey = encodeURIComponent(id);
    const response = await apiClient.post(
      `${Endpoints.getRazorpayHashKeyVerify}/${hashKey}`,
      { rzp_payment_id },
    );
    return response?.data ?? null;
  } catch (error) {
    return null;
  }
};

export const getActiveCountries = async () => {
  const apiCall = async () => {
    const response = await apiClient.get(Endpoints.activeCountries);
    return response?.data;
  };

  return fetchAndCacheWithoutPage(apiCall, Endpoints.activeCountries);
};

export const syncCart = async (payload: {
  temp_user_id: string;
  user_id: string;
  cart_id: string;
}) => {
  return await apiClient.post(Endpoints.syncCart, payload);
};

/** localStorage key prefix for accumulated `others` data, keyed by cart ID */
const OTHERS_CACHE_KEY_PREFIX = 'zl_checkout_others_';

/** Retrieve the currently accumulated `others` object for a given cart from localStorage */
const getOthersCache = (cartId: number | string): Record<string, any> => {
  try {
    if (typeof window === 'undefined') return {};
    const raw = localStorage.getItem(`${OTHERS_CACHE_KEY_PREFIX}${cartId}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

/** Persist the accumulated `others` object for a given cart to localStorage */
const setOthersCache = (
  cartId: number | string,
  data: Record<string, any>,
): void => {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(
      `${OTHERS_CACHE_KEY_PREFIX}${cartId}`,
      JSON.stringify(data),
    );
  } catch {
    // ignore storage errors silently
  }
};

/**
 * Seed the local `others` cache from data returned by the API (called on page load).
 * This ensures subsequent blur events merge against the server's current state.
 */
export const syncOthersCache = (
  cartId: number | string,
  others: Record<string, any> | string | null | undefined,
): void => {
  if (!others) return;
  try {
    const parsed = typeof others === 'string' ? JSON.parse(others) : others;
    setOthersCache(cartId, parsed);
  } catch {
    // ignore parse errors
  }
};

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * Full temp-cart-user local cache  (mirrors the server's data object format)
 * Keyed by cart ID, expires after 30 days.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const TEMP_CART_USER_CACHE_PREFIX = 'zl_temp_cart_user_';
/** 30 days in milliseconds */
const TEMP_CART_USER_CACHE_TTL = 30 * 24 * 60 * 60 * 1000;

/**
 * Read the cached temp-cart-user record for a cart.
 * Returns null if missing or the entry has expired.
 */
const getTempCartUserCache = (
  cartId: number | string,
): Record<string, any> | null => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(`${TEMP_CART_USER_CACHE_PREFIX}${cartId}`);
    if (!raw) return null;
    const entry: { data: Record<string, any>; expiresAt: number } =
      JSON.parse(raw);
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(`${TEMP_CART_USER_CACHE_PREFIX}${cartId}`);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
};

/**
 * Merge `updates` into the existing cached record (or create a new entry)
 * and reset the 30-day TTL.
 */
const updateTempCartUserCache = (
  cartId: number | string,
  updates: Record<string, any>,
): void => {
  try {
    if (typeof window === 'undefined') return;
    const existing = getTempCartUserCache(cartId) ?? {};
    const merged = { ...existing, ...updates };
    const entry = {
      data: merged,
      expiresAt: Date.now() + TEMP_CART_USER_CACHE_TTL,
    };
    localStorage.setItem(
      `${TEMP_CART_USER_CACHE_PREFIX}${cartId}`,
      JSON.stringify(entry),
    );
  } catch {
    // ignore storage errors silently
  }
};

const FORM_TO_API_DIRECT_FIELDS: Record<string, string> = {
  fullname: 'name',
  phone: 'phone',
  email: 'email',
  address: 'address',
  countryId: 'country_id',
  stateId: 'state_id',
  cityId: 'city_id',
  pincode: 'postal_code',
  phoneCountryCode: 'country_code',
};

export const tempCartUsers = async (params: {
  cart_id: number;
  fieldName: string;
  fieldValue: any;
  zl_uid?: string;
}) => {
  try {
    const apiFieldName = FORM_TO_API_DIRECT_FIELDS[params.fieldName];
    const directFields: Record<string, any> = {};
    let othersPayload: Record<string, any> | undefined;

    if (apiFieldName) {
      directFields[apiFieldName] = params.fieldValue;
      // phoneCountryCode maps to both country_code and iso2
      if (params.fieldName === 'phoneCountryCode') {
        directFields['iso2'] = params.fieldValue;
      }
    } else {
      // Merge the new field into the existing accumulated `others` cache
      // so previous extra fields are never lost on subsequent saves.
      const existing = getOthersCache(params.cart_id);
      const merged = { ...existing, [params.fieldName]: params.fieldValue };
      setOthersCache(params.cart_id, merged);
      othersPayload = merged;
    }

    const basePayload: Record<string, any> = {
      cart_id: params.cart_id,
      ...directFields,
      ...(params.zl_uid && { zl_uid: params.zl_uid }),
      ...(othersPayload && { others: JSON.stringify(othersPayload) }),
    };

    const payload = await getPayload(basePayload);
    await apiClient.post(Endpoints.updateAddressOnBlur, payload);

    // Mirror the saved field(s) into the 30-day local cache (server-data format)
    const cacheUpdate: Record<string, any> = { ...directFields };
    if (othersPayload) {
      // `others` is stored as a JSON string in both server and local cache
      cacheUpdate.others = JSON.stringify(othersPayload);
    }
    updateTempCartUserCache(params.cart_id, cacheUpdate);
  } catch (error) {
    if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
      console.error(error);
    }
  }
};

/**
 * Fetches previously saved temp-cart-user data for a cart.
 *
 * Strategy:
 *  1. Check the 30-day localStorage cache — avoids a server round-trip on every
 *     page refresh once the user has started filling in the form.
 *  2. On cache miss (first visit or expired), fall back to the server, then seed
 *     both the full-data cache and the `others` accumulator cache for next time.
 */
export const getTempCartUser = async (cartId: number | string) => {
  // 1. Local cache hit — return immediately without calling the server
  const cached = getTempCartUserCache(cartId);
  if (cached) {
    return { data: cached };
  }

  // 2. Cache miss — fetch from server
  const response = await apiClient.get(Endpoints.getTempCartUser(cartId));
  const serverData = response?.data?.data;
  if (serverData) {
    // Seed the full-data cache so next refresh is instant
    updateTempCartUserCache(cartId, serverData);
    // Also seed the `others` accumulator so subsequent blur-saves merge correctly
    syncOthersCache(cartId, serverData.others);
  }
  return response?.data;
};
